import React, { useState, useEffect, useLayoutEffect } from "react";
import { Button, CircularProgress } from "@mui/material";
import { ChannelActions } from "@Store";
import { useDispatch } from "react-redux";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import UploadImgDialogContent from "./UploadImgDialogContent";
import { postMediaImage } from "@Services/API/Media";
import { Controller, useForm } from "react-hook-form";
import { IChannel, IUserData } from "@Interfaces";
import { ImageType } from "@Interfaces/Entities/Images";
import { useTranslation } from "react-i18next";

interface IChannelImages {
    channel?: IChannel;
    imageType: ImageType;
    user?: IUserData;
    saveImage?: (Imgdata: { avatar: string; public_url: string }) => void;
    tempImageData?: {
        avatar: string;
        public_url: string;
    };
}

type UploadCoverFormValues = {
    image: string;
};

const UploadCoverContent: React.FC<IChannelImages> = (props) => {
    const { t } = useTranslation();
    const { channel, imageType, user, saveImage, tempImageData } = props;
    const dispatch = useDispatch<any>();
    const [hasCover, setHasCover] = useState(false);
    const [hasProfileImg, setHasProfileImg] = useState(false);
    const [hasUserProfileImg, setUserHasProfileImg] = useState(false);
    const [hasUserProfileImgTemp, setUserHasProfileImgTemp] = useState(false);
    const [publicUrlState, setPublicUrlState] = useState("");
    const [currentImg, setCurrentImg] = useState() as any;
    const [actionLoading, setActionLoading] = useState<HTMLElement>();
    const [actionSubmit, setActionSubmit] = useState<HTMLElement>();
    useEffect(() => {
        switch (imageType) {
            case ImageType.ChannelCover:
                const coverPublicUlr = channel?.cover?.image?.public_url || "";
                const channelCover = channel?.cover?.image?.fid || "";

                if (channelCover && coverPublicUlr) {
                    setHasCover(true);
                    setCurrentImg(channelCover);
                    setPublicUrlState(coverPublicUlr);
                }

                break;
            case ImageType.ChannelProfile:
                const profilePublicUlr =
                    channel?.profile_image?.public_url || "";
                const channelProfileImg = channel?.profile_image?.fid || "";

                if (channelProfileImg && profilePublicUlr) {
                    setHasProfileImg(true);
                    setCurrentImg(channelProfileImg);
                    setPublicUrlState(profilePublicUlr);
                }
                break;
            case ImageType.UserProfile:
                const tempAvatar = tempImageData?.avatar || "";
                const userTempUrl = tempImageData?.public_url || "";

                if (tempAvatar && userTempUrl) {
                    setUserHasProfileImgTemp(true);
                    setCurrentImg(tempAvatar);
                    setPublicUrlState(userTempUrl);
                } else {
                    const userProfileImg = user?.avatar
                        ? user?.avatar["fid"]
                        : "";

                    const userProfileImgPublicUrl = user?.avatar
                        ? user?.avatar["public_url"]
                        : "";

                    if (userProfileImg && userProfileImgPublicUrl) {
                        setUserHasProfileImg(true);
                        setCurrentImg(userProfileImg);
                        setPublicUrlState(userProfileImgPublicUrl);
                    }
                }
                break;
            default:
                return;
        }
    }, [channel, imageType, setCurrentImg, tempImageData, user]);

    const { handleSubmit, setValue, control } = useForm<UploadCoverFormValues>({
        defaultValues: {
            image: "",
        },
    });
    useLayoutEffect(() => {
        setActionLoading(
            document.getElementById("channel-cover-action-loading")!
        );
        setActionSubmit(
            document.getElementById("channel-cover-action-submit")!
        );
    }, []);

    const handleDraftImage = async (formData: () => Promise<any>) => {
        actionLoading!.classList.toggle("d-none");
        actionSubmit!.classList.toggle("d-none");

        const body = await formData();
        const { image_id, publicUrl } = await postMediaImage(body);

        if (publicUrl && !publicUrlState) {
            setPublicUrlState(publicUrl);
        }

        if (image_id) {
            setValue("image", image_id);
            setCurrentImg(image_id);
            actionLoading!.classList.toggle("d-none");
            actionSubmit!.classList.toggle("d-none");
        }
    };

    const handleEmptyCoverUrl = () => {
        switch (imageType) {
            case ImageType.ChannelCover:
                setHasCover(false);
                break;
            case ImageType.ChannelProfile:
                setHasProfileImg(false);
                break;
            case ImageType.UserProfile:
                setUserHasProfileImg(false);
                setUserHasProfileImgTemp(false);
                break;
        }
        setCurrentImg("");
    };

    const onSubmit = async (_data: UploadCoverFormValues) => {
        if (!currentImg) {
            dispatch(dialogActions.hideDialog());
            return;
        }
        if (imageType === ImageType.UserProfile) {
            let Imgdata = {
                avatar: currentImg,
                public_url: publicUrlState,
            };
            if (saveImage) saveImage(Imgdata);
        } else {
            let Imgdata = {};
            if (imageType === ImageType.ChannelCover) {
                Imgdata = {
                    title: channel?.title,
                    public_url: publicUrlState,
                    cover_image: currentImg,
                };
            }
            if (imageType === ImageType.ChannelProfile) {
                Imgdata = {
                    title: channel?.title,
                    public_url: publicUrlState,
                    profile_image: currentImg,
                };
            }

            dispatch(
                ChannelActions.updateChannel(channel as IChannel, Imgdata)
            );
        }
        dispatch(dialogActions.hideDialog());
    };

    const coverUrl = hasCover
        ? `${channel?.cover?.image?.public_url}/${channel?.cover?.image?.fid}`
        : "";

    const profileImgUrl = hasProfileImg
        ? `${channel?.profile_image?.public_url}/${channel?.profile_image?.fid}`
        : "";

    const userProfileImgUrl = hasUserProfileImgTemp
        ? `${publicUrlState}/${currentImg}`
        : hasUserProfileImg
        ? `${user?.avatar?.public_url}/${user?.avatar?.fid}`
        : "";

    return (
        <>
            <form
                id="channel-dialog"
                noValidate
                onSubmit={handleSubmit(onSubmit)}
                encType="multipart/form-data"
            >
                <Controller
                    control={control}
                    name={`image`}
                    render={() => (
                        <UploadImgDialogContent
                            onSetBuffer={(formValues) =>
                                handleDraftImage(formValues)
                            }
                            hue={channel?.cover?.hue || 22}
                            coverUrl={coverUrl}
                            emptyCoverUrl={handleEmptyCoverUrl}
                            imageType={imageType}
                            profileImgUrl={profileImgUrl}
                            userProfileImgUrl={userProfileImgUrl}
                        />
                    )}
                />

                <Button
                    id="channel-cover-submit"
                    type="submit"
                    size="small"
                    style={{
                        display: "none",
                    }}
                >
                    {t("dialogs.save")}
                </Button>
            </form>
        </>
    );
};

const UploadCoverActions: React.FC<any> = () => {
    const { t } = useTranslation();
    const handleChannelCoverSave = () =>
        document.getElementById("channel-cover-submit")?.click();

    return (
        <>
            <Button
                id="channel-cover-action-submit"
                onClick={handleChannelCoverSave}
                variant="contained"
                color="primary"
            >
                {t("dialogs.select")}
            </Button>
            <Button
                className="d-none"
                id="channel-cover-action-loading"
                variant="contained"
                color="primary"
                disabled
            >
                <CircularProgress size={20} />
            </Button>
        </>
    );
};

export default {
    actions: UploadCoverActions,
    content: UploadCoverContent,
    title: "Upload Image",
};
